import { useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import CursorFX from "@/components/CursorFX";
import ScrollProgress from "@/components/ScrollProgress";
import { api } from "@/lib/api";

export default function StoreLayout() {
  const { pathname, hash } = useLocation();
  const { data: s } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.1 });
    window.__lenis = lenis;
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); window.__lenis = null; };
  }, []);

  useEffect(() => {
    if (hash) {
      setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 150);
    } else if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <div className="grain min-h-screen bg-[#050505]" data-testid="store-layout">
      {s?.announcement_enabled && s?.announcement && (
        <div className="bg-[#e10032] text-white text-center text-[11px] md:text-xs font-bold tracking-[0.22em] uppercase py-2 px-4" data-testid="announcement-bar">{s.announcement}</div>
      )}
      <ScrollProgress />
      <CursorFX />
      <Navbar />
      <main><Outlet context={{ settings: s }} /></main>
      <footer className="relative mt-32 border-t border-white/10 overflow-hidden" data-testid="footer">
        <img src="/brand/art_ruins.webp" alt="" className="absolute inset-x-0 bottom-0 w-full h-48 object-cover opacity-30" />
        <div className="relative max-w-[1500px] mx-auto px-5 md:px-10 py-16 grid md:grid-cols-[1.4fr_1fr_1fr] gap-12">
          <div>
            <img src="/brand/logo.webp" alt="Bomber RSNS" className="w-48 mb-5" />
            <p className="text-[#999] max-w-sm text-sm">One-of-one Old School RuneScape names. Buy a name, complete payment, and its secure transfer details unlock on your order.</p>
          </div>
          <div className="space-y-2 text-sm">
            <div className="overline-label mb-3">Navigate</div>
            <Link className="block text-[#bbb] hover:text-white transition-colors" to="/shop">RSN vault</Link>
            <Link className="block text-[#bbb] hover:text-white transition-colors" to="/track">Track an order</Link>
            <Link className="block text-[#bbb] hover:text-white transition-colors" to="/login">Account</Link>
          </div>
          <div className="space-y-2 text-sm">
            <div className="overline-label mb-3">Contact</div>
            {s?.discord_url && <a className="block text-[#bbb] hover:text-white transition-colors" href={s.discord_url} target="_blank" rel="noreferrer" data-testid="footer-discord-link">Discord</a>}
            {s?.support_email && <a className="block text-[#bbb] hover:text-white transition-colors" href={`mailto:${s.support_email}`}>{s.support_email}</a>}
          </div>
        </div>
        <div className="relative border-t border-white/10 py-5 text-center text-[10px] tracking-[0.25em] uppercase text-[#666]">Bomber RSNS. Not affiliated with Jagex Ltd.</div>
      </footer>
      <CartDrawer />
    </div>
  );
}
